import React from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Building2, MapPin, Landmark, Plus, X, Users } from "lucide-react";

/** DMS + Menu combobox suggestions. User can type any other value. */
export const DMS_SUGGESTIONS = [
  "Reynolds & Reynolds", "CDK Global", "Dealertrack", "DealerSocket",
  "Auto/Mate", "PBS Systems", "Dominion Vue", "Advent Resources",
];
export const MENU_SUGGESTIONS = [
  "MenuMetric", "EasyCare", "F&I Express", "MaximTrak", "StoneEagle",
  "iMenu", "AutoLoop", "Reynolds Rate & Spec",
];

/** Empty scaffolds. */
export const emptyLocation = () => ({
  name: "", physical_address: "", phone: "",
  fi_managers: [""],
  general_manager: "", fi_director: "", office_manager: "",
  labor_rate: "", dms: "", menu_provider: "", ein: "",
});
export const emptyAch = () => ({
  label: "", bank_name: "", routing: "", account: "", account_holder: "",
  location_ids: [],
});

/**
 * Reusable dealer intake form. Renders dealer-level fields, a dynamic list
 * of location cards, and an ACH group builder that ties each ACH record to
 * one or more locations.
 *
 * Props:
 *   value  — { dealer_name, dba, physical_address, phone, ein, dealer_principal,
 *              contact_name, contact_email, contact_phone, notes,
 *              locations: [], ach_groups: [] }
 *   onChange(nextValue)
 *   hideContactBlock — hides the contact_name/email/phone block (agent flow
 *      might not want to re-collect this if the agent is the contact)
 */
export function DealerIntakeForm({ value, onChange, hideContactBlock = false }) {
  const set = (patch) => onChange({ ...value, ...patch });

  // ----- Location mutations -----
  const addLocation = () => set({ locations: [...(value.locations || []), emptyLocation()] });
  const removeLocation = (idx) => {
    const locs = [...(value.locations || [])];
    locs.splice(idx, 1);
    // Cascade: drop this location's local index from every ACH group. We use
    // index-based selection in the client (ids come from server), so on
    // removal we just filter by index reference here.
    const nextAch = (value.ach_groups || []).map((g) => ({
      ...g, location_ids: (g.location_ids || []).filter((_, i) => i !== idx),
    }));
    set({ locations: locs, ach_groups: nextAch });
  };
  const patchLocation = (idx, patch) => {
    const locs = [...(value.locations || [])];
    locs[idx] = { ...locs[idx], ...patch };
    set({ locations: locs });
  };
  const patchFiManager = (idx, mIdx, name) => {
    const locs = [...(value.locations || [])];
    const managers = [...(locs[idx].fi_managers || [])];
    managers[mIdx] = name;
    locs[idx] = { ...locs[idx], fi_managers: managers };
    set({ locations: locs });
  };
  const addFiManager = (idx) => {
    const locs = [...(value.locations || [])];
    locs[idx] = { ...locs[idx], fi_managers: [...(locs[idx].fi_managers || []), ""] };
    set({ locations: locs });
  };
  const removeFiManager = (idx, mIdx) => {
    const locs = [...(value.locations || [])];
    const managers = [...(locs[idx].fi_managers || [])];
    managers.splice(mIdx, 1);
    locs[idx] = { ...locs[idx], fi_managers: managers.length ? managers : [""] };
    set({ locations: locs });
  };

  // ----- ACH group mutations -----
  const addAch = () => set({ ach_groups: [...(value.ach_groups || []), emptyAch()] });
  const removeAch = (idx) => {
    const groups = [...(value.ach_groups || [])];
    groups.splice(idx, 1);
    set({ ach_groups: groups });
  };
  const patchAch = (idx, patch) => {
    const groups = [...(value.ach_groups || [])];
    groups[idx] = { ...groups[idx], ...patch };
    set({ ach_groups: groups });
  };
  const toggleAchLocation = (idx, locIndex) => {
    const groups = [...(value.ach_groups || [])];
    const ids = new Set(groups[idx].location_ids || []);
    if (ids.has(locIndex)) ids.delete(locIndex);
    else ids.add(locIndex);
    groups[idx] = { ...groups[idx], location_ids: Array.from(ids) };
    set({ ach_groups: groups });
  };

  const locations = value.locations || [];
  const achGroups = value.ach_groups || [];

  return (
    <div className="space-y-6">
      {/* ============= Dealer info ============= */}
      <Card className="bg-white border border-[#E9ECEF] rounded-sm p-6" data-testid="dealer-info-card">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-[#0B132B] mb-4">
          <Building2 size={14} className="text-[#D4AF37]" /> Dealer info (fills once, placed anywhere)
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Dealership legal name *</Label>
            <Input value={value.dealer_name || ""} onChange={(e) => set({ dealer_name: e.target.value })} data-testid="intake-dealer-name" />
          </div>
          <div>
            <Label>DBA (if applicable)</Label>
            <Input value={value.dba || ""} onChange={(e) => set({ dba: e.target.value })} data-testid="intake-dba" />
          </div>
          <div className="md:col-span-2">
            <Label>Physical address</Label>
            <Input value={value.physical_address || ""} onChange={(e) => set({ physical_address: e.target.value })} placeholder="Street, City, State ZIP" data-testid="intake-address" />
          </div>
          <div>
            <Label>Phone</Label>
            <Input value={value.phone || ""} onChange={(e) => set({ phone: e.target.value })} data-testid="intake-phone" />
          </div>
          <div>
            <Label>EIN / Tax ID</Label>
            <Input value={value.ein || ""} onChange={(e) => set({ ein: e.target.value })} data-testid="intake-ein" />
          </div>
          <div className="md:col-span-2">
            <Label>Dealer Principal</Label>
            <Input value={value.dealer_principal || ""} onChange={(e) => set({ dealer_principal: e.target.value })} placeholder="Full name of principal / owner" data-testid="intake-principal" />
          </div>
        </div>

        {!hideContactBlock && (
          <div className="mt-6 pt-6 border-t border-[#E9ECEF]">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0B132B] mb-3">Primary contact (you)</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Your name *</Label>
                <Input value={value.contact_name || ""} onChange={(e) => set({ contact_name: e.target.value })} data-testid="intake-contact-name" />
              </div>
              <div>
                <Label>Your phone (optional)</Label>
                <Input value={value.contact_phone || ""} onChange={(e) => set({ contact_phone: e.target.value })} data-testid="intake-contact-phone" />
              </div>
              <div className="md:col-span-2">
                <Label>Your email *</Label>
                <Input type="email" value={value.contact_email || ""} onChange={(e) => set({ contact_email: e.target.value })} data-testid="intake-contact-email" />
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* ============= Locations ============= */}
      <Card className="bg-white border border-[#E9ECEF] rounded-sm p-6" data-testid="dealer-locations-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-[#0B132B]">
            <MapPin size={14} className="text-[#D4AF37]" /> Locations ({locations.length})
          </div>
          <Button type="button" onClick={addLocation} className="h-9 rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white" data-testid="intake-add-location">
            <Plus size={13} className="mr-1.5" /> Add location
          </Button>
        </div>
        <p className="text-xs text-[#6C757D] mb-4">
          Each physical rooftop has its own info sheet. F&I managers, GM, labor rate, DMS, and menu are per-location.
        </p>

        {locations.length === 0 && (
          <div className="text-sm italic text-[#6C757D] py-8 text-center border border-dashed border-[#E9ECEF] rounded-sm">
            No locations yet — click "Add location" to add the first rooftop.
          </div>
        )}

        <div className="space-y-4">
          {locations.map((loc, idx) => (
            <LocationCard
              key={idx}
              idx={idx}
              loc={loc}
              patch={(p) => patchLocation(idx, p)}
              patchFi={(mIdx, name) => patchFiManager(idx, mIdx, name)}
              addFi={() => addFiManager(idx)}
              removeFi={(mIdx) => removeFiManager(idx, mIdx)}
              remove={() => removeLocation(idx)}
            />
          ))}
        </div>
      </Card>

      {/* ============= ACH groups ============= */}
      <Card className="bg-white border border-[#E9ECEF] rounded-sm p-6" data-testid="dealer-ach-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-[#0B132B]">
            <Landmark size={14} className="text-[#D4AF37]" /> ACH ({achGroups.length})
          </div>
          <Button type="button" onClick={addAch} className="h-9 rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white" data-testid="intake-add-ach" disabled={locations.length === 0}>
            <Plus size={13} className="mr-1.5" /> Add ACH
          </Button>
        </div>
        <p className="text-xs text-[#6C757D] mb-4">
          Add one ACH per bank account. Tick every location this account funds — if all rooftops share one bank account, one ACH covers them all; otherwise create a separate ACH per rooftop.
        </p>

        {locations.length === 0 && (
          <div className="text-sm italic text-[#6C757D] py-6 text-center border border-dashed border-[#E9ECEF] rounded-sm">
            Add at least one location before ACH info.
          </div>
        )}

        <div className="space-y-4">
          {achGroups.map((g, idx) => (
            <AchGroupCard
              key={idx}
              idx={idx}
              group={g}
              locations={locations}
              patch={(p) => patchAch(idx, p)}
              toggleLocation={(locIndex) => toggleAchLocation(idx, locIndex)}
              remove={() => removeAch(idx)}
            />
          ))}
        </div>
      </Card>
    </div>
  );
}

/** Convert form-state (client-side idx-based ach location_ids) into the
 * server payload shape. Because the client doesn't know server-generated
 * location ids yet, we send `location_indices` mapped to a stable id per
 * client-side location on the server. We send `location_ids` filled with
 * synthetic tokens so the backend can resolve them cleanly. */
export function serializeIntake(value) {
  // Assign synthetic ids per location so ACH groups can reference them.
  const locs = (value.locations || []).map((l, i) => ({
    id: `loc_${i}`,
    ...l,
  }));
  const achs = (value.ach_groups || []).map((g) => ({
    ...g,
    location_ids: (g.location_ids || []).map((i) => `loc_${i}`),
  }));
  return {
    ...value,
    locations: locs,
    ach_groups: achs,
  };
}

// ============== Sub-components ==============
function LocationCard({ idx, loc, patch, patchFi, addFi, removeFi, remove }) {
  return (
    <div className="border-2 border-[#E9ECEF] rounded-sm p-4 relative" data-testid={`location-card-${idx}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#0B132B]">
          Location #{idx + 1}
        </div>
        <button type="button" onClick={remove} className="text-[#6C757D] hover:text-red-600" data-testid={`location-remove-${idx}`} aria-label="Remove location">
          <X size={16} />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <div className="md:col-span-2">
          <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#0B132B]">Location name / label</Label>
          <Input value={loc.name || ""} onChange={(e) => patch({ name: e.target.value })} placeholder="e.g. Main Store, Downtown, West Side" data-testid={`location-name-${idx}`} />
        </div>
        <div className="md:col-span-2">
          <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#0B132B]">Physical address</Label>
          <Input value={loc.physical_address || ""} onChange={(e) => patch({ physical_address: e.target.value })} data-testid={`location-address-${idx}`} />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#0B132B]">Location phone</Label>
          <Input value={loc.phone || ""} onChange={(e) => patch({ phone: e.target.value })} data-testid={`location-phone-${idx}`} />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#0B132B]">Labor rate ($/hr)</Label>
          <Input value={loc.labor_rate || ""} onChange={(e) => patch({ labor_rate: e.target.value })} placeholder="150" data-testid={`location-labor-rate-${idx}`} />
        </div>
      </div>

      {/* People */}
      <div className="border-t border-[#E9ECEF] pt-4 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <Users size={12} className="text-[#0B132B]" />
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#0B132B]">People</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#0B132B]">General Manager</Label>
            <Input value={loc.general_manager || ""} onChange={(e) => patch({ general_manager: e.target.value })} data-testid={`location-gm-${idx}`} />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#0B132B]">F&I Director (optional)</Label>
            <Input value={loc.fi_director || ""} onChange={(e) => patch({ fi_director: e.target.value })} data-testid={`location-fi-director-${idx}`} />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#0B132B]">Office Manager</Label>
            <Input value={loc.office_manager || ""} onChange={(e) => patch({ office_manager: e.target.value })} data-testid={`location-office-mgr-${idx}`} />
          </div>
        </div>

        {/* F&I Managers — dynamic list */}
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#0B132B]">F&I Managers</Label>
            <button type="button" onClick={addFi} className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#D4AF37] hover:text-[#0B132B]" data-testid={`location-add-fi-${idx}`}>
              + Add another
            </button>
          </div>
          <div className="space-y-2">
            {(loc.fi_managers || [""]).map((m, mIdx) => (
              <div key={mIdx} className="flex items-center gap-2">
                <Input value={m} onChange={(e) => patchFi(mIdx, e.target.value)} placeholder={`F&I Manager #${mIdx + 1}`} className="flex-1" data-testid={`location-fi-manager-${idx}-${mIdx}`} />
                {(loc.fi_managers || []).length > 1 && (
                  <button type="button" onClick={() => removeFi(mIdx)} className="text-[#6C757D] hover:text-red-600 shrink-0" aria-label="Remove F&I manager" data-testid={`location-fi-remove-${idx}-${mIdx}`}>
                    <X size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Systems */}
      <div className="border-t border-[#E9ECEF] pt-4">
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#0B132B] mb-3">Systems</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <ComboField
            label="DMS Provider" list={`dms-list-${idx}`} suggestions={DMS_SUGGESTIONS}
            value={loc.dms || ""} onChange={(v) => patch({ dms: v })}
            testId={`location-dms-${idx}`}
          />
          <ComboField
            label="Menu Provider" list={`menu-list-${idx}`} suggestions={MENU_SUGGESTIONS}
            value={loc.menu_provider || ""} onChange={(v) => patch({ menu_provider: v })}
            testId={`location-menu-${idx}`}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#0B132B] mb-1">Location EIN <span className="text-[#6C757D] font-normal normal-case tracking-normal">(if different from dealer EIN)</span></div>
            <Input value={loc.ein || ""} onChange={(e) => patch({ ein: e.target.value })} placeholder="12-3456789" className="h-10 rounded-sm" data-testid={`location-ein-${idx}`} />
          </div>
        </div>
      </div>
    </div>
  );
}

function ComboField({ label, list, suggestions, value, onChange, testId }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#0B132B]">{label}</Label>
      <Input
        list={list}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Start typing or pick a suggestion"
        data-testid={testId}
      />
      <datalist id={list}>
        {suggestions.map((s) => <option key={s} value={s} />)}
      </datalist>
    </div>
  );
}

function AchGroupCard({ idx, group, locations, patch, toggleLocation, remove }) {
  return (
    <div className="border-2 border-[#E9ECEF] rounded-sm p-4 relative" data-testid={`ach-card-${idx}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#0B132B]">
          ACH #{idx + 1}
        </div>
        <button type="button" onClick={remove} className="text-[#6C757D] hover:text-red-600" data-testid={`ach-remove-${idx}`} aria-label="Remove ACH">
          <X size={16} />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#0B132B]">Label (optional)</Label>
          <Input value={group.label || ""} onChange={(e) => patch({ label: e.target.value })} placeholder="e.g. Main ACH" data-testid={`ach-label-${idx}`} />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#0B132B]">Account holder name</Label>
          <Input value={group.account_holder || ""} onChange={(e) => patch({ account_holder: e.target.value })} data-testid={`ach-holder-${idx}`} />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#0B132B]">Bank name</Label>
          <Input value={group.bank_name || ""} onChange={(e) => patch({ bank_name: e.target.value })} data-testid={`ach-bank-${idx}`} />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#0B132B]">Routing #</Label>
          <Input value={group.routing || ""} onChange={(e) => patch({ routing: e.target.value })} data-testid={`ach-routing-${idx}`} />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#0B132B]">Account #</Label>
          <Input value={group.account || ""} onChange={(e) => patch({ account: e.target.value })} data-testid={`ach-account-${idx}`} />
        </div>
      </div>

      <div>
        <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#0B132B] mb-2 block">
          Locations covered by this ACH
        </Label>
        {locations.length === 0 ? (
          <div className="text-xs italic text-[#6C757D]">Add a location first.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {locations.map((loc, locIndex) => {
              const on = (group.location_ids || []).includes(locIndex);
              return (
                <label key={locIndex}
                       className={`flex items-center gap-2 p-2 rounded-sm border-2 cursor-pointer transition-colors ${on ? "border-[#D4AF37] bg-[#D4AF37]/5" : "border-[#E9ECEF] hover:border-[#0B132B]/30"}`}
                       data-testid={`ach-loc-toggle-${idx}-${locIndex}`}>
                  <Checkbox checked={on} onCheckedChange={() => toggleLocation(locIndex)} />
                  <span className="text-sm text-[#0B132B]">{loc.name || `Location #${locIndex + 1}`}</span>
                </label>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
